const activeReaders = new Set<HTMLElement>();

export const pageViewportCss = `:host([page-viewport]) [data-page-scroll] .safe-box-layer:empty{display:none}:host([page-viewport]){display:block;height:var(--archive-view-height,100dvh);overflow:hidden}:host([page-viewport]) .scene{height:var(--archive-view-height,100dvh)!important;min-height:0!important;max-height:var(--archive-view-height,100dvh);aspect-ratio:auto!important;overflow:hidden!important}:host([page-viewport]) [data-page-scroll]{height:var(--archive-list-height)!important;max-height:var(--archive-list-height)!important;overflow-x:hidden!important;overflow-y:auto!important;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-gutter:stable;touch-action:pan-y;align-content:start;translate:0 var(--archive-list-shift,0px)}:host([page-viewport]) [data-archive-fade-bottom]{-webkit-mask-image:linear-gradient(to bottom,#000 calc(100% - min(48px,20%)),transparent 100%);mask-image:linear-gradient(to bottom,#000 calc(100% - min(48px,20%)),transparent 100%)}:host([page-viewport]) [data-media-scroll]{scrollbar-gutter:auto}:host([page-viewport]) [data-media-flow]{height:auto!important}:host([page-viewport]) [data-page-scroll]:focus-visible{outline:3px solid var(--accent);outline-offset:-3px}`;

export function installPageViewport(
  host: HTMLElement,
  root: ShadowRoot,
  previousScroll = 0,
  mode: "archive" | "daily" = "archive",
  pageArtworkRatio?: number,
) {
  const scrollPage = pageArtworkRatio !== undefined;
  if (!document.getElementById("pinedale-fixed-page-style")) {
    const style = document.createElement("style");
    style.id = "pinedale-fixed-page-style";
    style.textContent =
      "html.pinedale-fixed-page,html.pinedale-fixed-page body{height:100dvh!important;overflow:hidden!important;overscroll-behavior:none;overflow-anchor:none}";
    document.head.append(style);
  }
  if (!scrollPage) {
    activeReaders.add(host);
    document.documentElement.classList.add("pinedale-fixed-page");
    window.scrollTo(0, 0);
    host.setAttribute("fixed-viewport", "");
  }
  host.setAttribute("page-viewport", "");
  const controller = new AbortController();
  const signal = controller.signal;
  const visible = (selector: string) =>
    [...root.querySelectorAll<HTMLElement>(selector)].find(
      (element) => element.getClientRects().length,
    );
  const grid = mode === "archive" ? visible(".archive-grid") : undefined;
  const scroller =
    mode === "archive"
      ? (grid?.closest<HTMLElement>(".archive-gallery") ?? grid)
      : visible(
          ".prose,.page-block:has(>.compact-audio-player),.page-block:has(>iframe.media-player)",
        );
  const readingBox =
    mode === "daily" ? scroller?.closest<HTMLElement>(".image-safe-box") : null;
  if (scroller) {
    scroller.setAttribute("data-page-scroll", "");
    scroller.setAttribute(
      mode === "archive" ? "data-archive-scroll" : "data-reading-scroll",
      "",
    );
    if (mode === "daily" && !scroller.matches(".prose")) {
      scroller.setAttribute("data-media-scroll", "");
      if (!readingBox) scroller.setAttribute("data-media-flow", "");
    }
    scroller.setAttribute("role", "region");
    scroller.setAttribute(
      "aria-label",
      mode === "archive" ? "Previous readings" : "Today's reading",
    );
    scroller.tabIndex = 0;
  }
  const updateFade = () => {
    scroller?.toggleAttribute(
      "data-archive-fade-bottom",
      mode === "archive" &&
        scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > 1,
    );
  };
  scroller?.addEventListener("scroll", updateFade, { passive: true, signal });
  const contentObserver = new ResizeObserver(updateFade);
  if (scroller) {
    contentObserver.observe(scroller);
    for (const card of scroller.querySelectorAll(".card"))
      contentObserver.observe(card);
  }
  const navigation = [...root.querySelectorAll<HTMLElement>("nav")]
    .filter((nav) => nav.getClientRects().length && !scroller?.contains(nav))
    .map((nav) => ({
      nav,
      box: nav.closest<HTMLElement>("[data-safe-box]") ?? nav.parentElement!,
    }));
  const fitNavigation = (bottom: number) => {
    for (const { nav, box } of navigation) {
      box.style.removeProperty("translate");
      const rect = nav.getBoundingClientRect();
      const shift = Math.min(0, bottom - 12 - rect.bottom);
      if (shift) box.style.translate = `0 ${shift}px`;
    }
  };
  const update = () => {
    if (!host.isConnected) return;
    const hostTop = host.getBoundingClientRect().top;
    const top = scrollPage ? hostTop : Math.max(0, hostTop);
    const artworkOffset =
      parseFloat(getComputedStyle(host).getPropertyValue("--artwork-offset")) ||
      0;
    const bottom = scrollPage
      ? top +
        Math.max(
          window.innerHeight - hostTop - window.scrollY,
          host.clientWidth * pageArtworkRatio! + artworkOffset,
        )
      : (window.visualViewport?.height ?? window.innerHeight);
    host.style.setProperty(
      "--archive-view-height",
      `${Math.max(0, bottom - top)}px`,
    );
    fitNavigation(bottom);
    if (!scroller) return;
    scroller.style.removeProperty("--archive-list-shift");
    let rect = scroller.getBoundingClientRect();
    const minHeight = Math.min(160, Math.max(44, bottom - top - 24));
    const shift = Math.min(0, bottom - 12 - minHeight - rect.top);
    scroller.style.setProperty("--archive-list-shift", `${shift}px`);
    rect = scroller.getBoundingClientRect();
    let limit = bottom - 12;
    if (readingBox) {
      const block = scroller.closest<HTMLElement>(".page-block")!;
      limit = Math.min(
        limit,
        readingBox.getBoundingClientRect().bottom -
          parseFloat(getComputedStyle(block).paddingBottom),
      );
    }
    for (const { nav } of navigation) {
      const button =
        nav.querySelector("a,button")?.getBoundingClientRect() ??
        nav.getBoundingClientRect();
      if (
        button.left < rect.right &&
        button.right > rect.left &&
        button.top > rect.top
      )
        limit = Math.min(limit, button.top - 12);
    }
    scroller.style.setProperty(
      "--archive-list-height",
      `${Math.max(44, limit - rect.top)}px`,
    );
    fitNavigation(bottom);
    updateFade();
  };
  update();
  if (scroller) scroller.scrollTop = previousScroll;
  updateFade();
  const frame = requestAnimationFrame(update);
  const observer = new ResizeObserver(update);
  observer.observe(host);
  const header = document.getElementById("sp-header");
  if (header) observer.observe(header);
  window.addEventListener("resize", update, { signal });
  window.visualViewport?.addEventListener("resize", update, { signal });
  const canScroll = (event: Event) =>
    !scrollPage &&
    scroller &&
    !root.querySelector("dialog[open]") &&
    !event
      .composedPath()
      .some(
        (node) =>
          node instanceof Element &&
          node.matches(
            "input,textarea,select,audio,video,iframe,[contenteditable=true],[role=menu],[role=slider]",
          ),
      );
  document.addEventListener(
    "wheel",
    (event) => {
      if (
        !canScroll(event) ||
        event.ctrlKey ||
        event.composedPath().includes(scroller!)
      )
        return;
      event.preventDefault();
      scroller!.scrollTop +=
        event.deltaY *
        (event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? scroller!.clientHeight
            : 1);
    },
    { passive: false, signal },
  );
  let touchY: number | undefined;
  document.addEventListener(
    "touchstart",
    (event) => {
      touchY =
        event.touches.length === 1 ? event.touches[0].clientY : undefined;
    },
    { passive: true, signal },
  );
  document.addEventListener(
    "touchmove",
    (event) => {
      if (touchY === undefined || event.touches.length !== 1) return;
      const delta = touchY - event.touches[0].clientY;
      touchY = event.touches[0].clientY;
      if (!canScroll(event) || event.composedPath().includes(scroller!)) return;
      event.preventDefault();
      scroller!.scrollTop += delta;
    },
    { passive: false, signal },
  );
  document.addEventListener(
    "keydown",
    (event) => {
      if (
        !canScroll(event) ||
        event.defaultPrevented ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      )
        return;
      if (
        event.key === " " &&
        event.composedPath().some((node) => node instanceof HTMLButtonElement)
      )
        return;
      const step =
        event.key === "ArrowDown"
          ? 40
          : event.key === "ArrowUp"
            ? -40
            : event.key === "PageDown" || (event.key === " " && !event.shiftKey)
              ? scroller!.clientHeight * 0.85
              : event.key === "PageUp" || (event.key === " " && event.shiftKey)
                ? -scroller!.clientHeight * 0.85
                : 0;
      if (!step && !["Home", "End"].includes(event.key)) return;
      event.preventDefault();
      scroller!.scrollTo({
        top:
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? scroller!.scrollHeight
              : scroller!.scrollTop + step,
        behavior: "instant",
      });
    },
    { signal },
  );
  return {
    get scrollTop() {
      return scroller?.scrollTop ?? 0;
    },
    update,
    destroy() {
      controller.abort();
      observer.disconnect();
      contentObserver.disconnect();
      cancelAnimationFrame(frame);
      activeReaders.delete(host);
      if (!activeReaders.size)
        document.documentElement.classList.remove("pinedale-fixed-page");
      host.removeAttribute("fixed-viewport");
      host.removeAttribute("page-viewport");
      host.style.removeProperty("--archive-view-height");
    },
  };
}
